// listar-notificacoes.component.ts
import { Component, OnInit, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';

// Angular Material Imports
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MenuComponent } from '../../../shared/components/menu/menu.component';
import { EmptyListComponent } from '../../../shared/components/empty-list/empty-list.component';
import { LoadingBoxComponent } from '../../../shared/components/loading-box/loading-box.component';
import { ModuloEnum } from '../../../shared/enums/modulo.enum';
import { FuncionalidadeEnum } from '../../../shared/enums/funcionalidade.enum';
import { HistoricoMensagemStruct } from '../../../shared/services/structs/srv-notificacao/historico-mensagem.struct';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { MensagemAdminService } from '../../../shared/services/apis/srv-notificacao/mensagem-admin.service';
import { UsuarioAdminService } from '../../../shared/services/apis/usuario-admin/usuario-admin.service';
import { ListAllMensagemRequest } from '../../../shared/services/requests/srv-notificacao/list-all-mensagem.request';
import { HistoricoMensagemResponse } from '../../../shared/services/responses/srv-notificacao/historico-mensagem.response';
import { ListaUsuarioResponse } from '../../../shared/services/responses/usuario/lista-usuario.response';
import { ListaUsuarioAdminResponse } from '../../../shared/services/responses/usuario-admin/lista-usuario-admin.response';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { NotificacaoDetalheModalComponent } from './notificacao-detalhe-modal/notificacao-detalhe-modal.component';


@Component({
  selector: 'app-notificacao-lista',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    FormsModule,
    MenuComponent,
    EmptyListComponent,
    LoadingBoxComponent,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatDialogModule,
    MatTooltipModule
  ],
  templateUrl: './notificacao-lista.component.html',
  styleUrl: './notificacao-lista.component.css'
})
export class NotificacaoListaComponent implements OnInit {

  public moduloEnum = ModuloEnum.Suporte;
  public funcionalidadeEnum = FuncionalidadeEnum.Suporte_Notificacao;

  public model: FormGroup;
  public displayedColumns: string[] = ['idMensagem', 'titulo', 'remetente', 'data', 'stats', 'status', 'actions'];
  public _dataSource = new MatTableDataSource<HistoricoMensagemStruct>();
  
  public _isLoading: boolean = false;
  public _paginaAtual: number = 0;
  public _ultimaPagina: boolean = false;
  public modoPainel: boolean = false;

  public selectedItem: HistoricoMensagemStruct | null = null;
  public justificativaExclusao: string = '';

  private userNamesCache: Map<number, string> = new Map();

  constructor(
    private formBuilder: FormBuilder,
    private alertService: AlertService,
    private mensagemAdminService: MensagemAdminService,
    private usuarioAdminService: UsuarioAdminService,
    private router: Router,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.model = this.formBuilder.group({
      titulo: [''],
      dataInicio: [null],
      dataFim: [null]
    });

    this.pesquisar();
  }

  novaNotificacao(): void {
    this.router.navigate(['/suporte/notificacao/enviar']);
  }

  clickPesquisar(): void {
    this._paginaAtual = 0;
    this._dataSource.data = [];
    this._ultimaPagina = false;
    this.pesquisar();
  }

  proximaPagina(): void {
    if (!this._ultimaPagina && !this._isLoading) {
      this._paginaAtual++;
      this.pesquisar();
    }
  }

  private pesquisar(): void {
    if (this._isLoading) return;

    this._isLoading = true;

    const request: ListAllMensagemRequest = {
      filtroTitulo: this.model.get('titulo')?.value,
      filtroDataInicio: this.model.get('dataInicio')?.value,
      filtroDataFim: this.model.get('dataFim')?.value
    };
    
    this.mensagemAdminService.getAll(request, this._paginaAtual).subscribe({
      next: (response: HistoricoMensagemResponse) => {
        if (response.isError) {
          this._isLoading = false;
          this.alertService.show('Aviso', response.errorDescription, AlertType.warning);
          return;
        }

        const novasMensagens = response.listaHistoricoMensagem || [];
        
        // Controle simples de "fim da lista" se vier vazio ou pouco
        if (novasMensagens.length === 0) {
            this._ultimaPagina = true;
        } 

        // Se for paginação infinita, concatenamos
        // Se for filtro novo, substituimos (tratado no clickPesquisar)
        if (this._paginaAtual === 0) {
            this._dataSource.data = novasMensagens;
        } else {
            this._dataSource.data = [...this._dataSource.data, ...novasMensagens];
        }
        // Lógica requisitada: Buscar nomes dos usuários APÓS buscar mensagens
        this.carregarNomesUsuarios(novasMensagens);
        
        this._isLoading = false;
      },
      error: (err) => {
        this._isLoading = false;
        this.alertService.show('Erro', 'Falha ao buscar notificações.', AlertType.error);
        console.error(err);
      }
    });
  }

  private carregarNomesUsuarios(mensagens: HistoricoMensagemStruct[]): void {
    const idsParaBuscar = new Set<number>();
    
    mensagens.forEach(msg => {
      if (msg.idUsuarioAdminRemetente && !this.userNamesCache.has(msg.idUsuarioAdminRemetente)) {
          idsParaBuscar.add(msg.idUsuarioAdminRemetente);
      }
      if (msg.idUsuarioAdminDelecao && !this.userNamesCache.has(msg.idUsuarioAdminDelecao)) {
          idsParaBuscar.add(msg.idUsuarioAdminDelecao);
      }
    });

    if (idsParaBuscar.size === 0) {
      this.aplicarNomesNaTabela();
      return;
    }

    const listaIds = Array.from(idsParaBuscar);
    this.usuarioAdminService.getAllByListId(listaIds).subscribe({
      next: (response: ListaUsuarioAdminResponse) => {
        if (response.isError) {
          this.alertService.show('Aviso', response.errorDescription, AlertType.warning);
          return;
        }
        
        response.listaUsuarioAdmin.forEach(user => {
          this.userNamesCache.set(user.idUsuarioAdmin, user.nomeUsuarioAdmin);
        });

        this.aplicarNomesNaTabela();
      },
      error: (err: any) => {
          console.warn('Não foi possível carregar nomes de usuários', err);
      }
    });
  }

  abrirDetalhes(item: HistoricoMensagemStruct): void {
    this.selectedItem = item;

    this.dialog.open(NotificacaoDetalheModalComponent, {
      width: '600px',
      maxHeight: '90vh',
      data: item
    });
  }

  confirmarExclusao(item: HistoricoMensagemStruct): void {
    this.selectedItem = item;
    this.justificativaExclusao = '';

    const dialogData = {
      title: `Tem certeza que deseja excluir a notificação ${this.selectedItem.tituloMensagem}`,
      innerHtml: "Esta ação removerá a mensagem da visualização dos clientes, e não pode ser revertida.",
      description: null,
      buttonConfirm: 'Confirmar',
      buttonClose: 'Cancelar',
      showTextarea: true,
      textareaLabel: 'Justificativa (mínimo 15 caracteres)',
      textareaRequired: true,
    };

    const dialogRef = this.dialog.open(ConfirmModalComponent, {
        width: '500px',
        data: dialogData
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.confirm) {
        this.justificativaExclusao = result.textareaValue;
        this.confirmarDeleteAction();
      }
    });
  }

  confirmarDeleteAction(): void {
    if (!this.selectedItem) return;

    this._isLoading = true;
    
    this.mensagemAdminService.delete(this.selectedItem.idMensagem, this.justificativaExclusao).subscribe({
      next: (response) => {
        this._isLoading = false;
        if (response.isError) {
          this.alertService.show('Erro', response.errorDescription, AlertType.error);
        } else {
          this.alertService.show('Sucesso', 'Notificação excluída.', AlertType.success);
          this.dialog.closeAll();
          this.clickPesquisar(); 
        }
      },
      error: (err) => {
        this._isLoading = false;
        this.alertService.show('Erro', 'Falha ao excluir notificação.', AlertType.error);
      }
    });
  }

  aplicarNomesNaTabela() {
      const dataAtualizada = this._dataSource.data.map(msg => {
        if (this.userNamesCache.has(msg.idUsuarioAdminRemetente)) {
          msg.nomeRemetente = this.userNamesCache.get(msg.idUsuarioAdminRemetente) || msg.nomeRemetente;
        }
        if (msg.idUsuarioAdminDelecao && this.userNamesCache.has(msg.idUsuarioAdminDelecao)) {
          msg.nomeUsuarioDelecao = this.userNamesCache.get(msg.idUsuarioAdminDelecao);
        }
        return msg;
      });
      this._dataSource.data = dataAtualizada;
    };
}